import {
  candidateColor,
  displayName,
  type Slug,
} from "../../lib/candidates.ts";
import { avatarImage, type AvatarKind, candidateImage } from "../assets.ts";

// Mix a hex colour toward white by `amt` (0 = full colour, 1 = white).
const tint = (hex: string, amt: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (ch: number) => Math.round(ch + (255 - ch) * amt);
  return `rgb(${mix((n >> 16) & 255)},${mix((n >> 8) & 255)},${mix(n & 255)})`;
};

export interface Controls {
  // Proximity action button (call each frame): show `label` running `onClick`,
  // or hide it when `key` is null. `hint` is an optional attention line shown
  // above the button with a down-arrow. De-duped by `key` so it's cheap/frame.
  setAction(
    key: string | null,
    label: string,
    onClick: () => void,
    hint?: string,
  ): void;
  pulseHelp(): void; // briefly nudge the ❓ button (e.g. after the welcome)
  dispose(): void;
}

// The on-screen HUD (fixed to the view — never moves with the room):
//   • your avatar face + a reveal/hide toggle (top-left).
//   • when revealed: a thin "I'm voting for ___" BANNER at the top of the
//     screen, MIRRORED (you hold it up facing outward → you see its back),
//     on a STICK descending toward you (Minecraft held-item vibe).
//   • a proximity action button that appears when you approach a booth (chat)
//     or a topic wall (ver cartelera).
export function createControls(opts: {
  kind: AvatarKind;
  votingFor: Slug;
  onHelp: () => void;
  onExit: () => void;
}): Controls {
  // ── Top-left: you + reveal toggle ────────────────────────────────────────
  const panel = document.createElement("div");
  panel.className =
    "fixed left-4 top-4 z-20 flex max-w-[260px] flex-col gap-2 rounded-xl border-2 border-black bg-white/90 p-3 text-sm";

  const meRow = document.createElement("div");
  meRow.className = "flex items-center gap-2";
  const meFace = document.createElement("img");
  meFace.src = avatarImage(opts.kind);
  meFace.alt = "tu avatar";
  meFace.className =
    "h-14 w-14 shrink-0 rounded-lg border-2 border-black bg-white object-contain";
  const meLabel = document.createElement("div");
  meLabel.className = "font-bold";
  meLabel.textContent = "Tú";
  meRow.append(meFace, meLabel);

  const msg = document.createElement("div");
  const toggle = document.createElement("button");
  toggle.className = "rounded-lg border-2 border-black px-3 py-1 font-bold";
  panel.append(meRow, msg, toggle);

  // ── Held banner on a diagonal stick (top of screen) ──────────────────────
  // Wide, slightly inclined, translucent, mirrored (seen from behind); the
  // stick leans down-left, as if held from the outer-left with your left arm.
  const held = document.createElement("div");
  held.className =
    "pointer-events-none fixed left-1/2 top-3 z-20 hidden -translate-x-1/2";

  // The stick, leaning down-LEFT toward your (off-screen) left hand — same side
  // as the joystick. Long, anchored near the banner's left edge.
  const stick = document.createElement("div");
  stick.className =
    "absolute left-8 top-10 h-[680px] w-3 origin-top rotate-[44deg] rounded border-x-2 border-black bg-white/80";

  // The banner — washed very subtly in the candidate's brand colour (light
  // tinted paper + a colour accent border), matching their booth halo.
  const banner = document.createElement("div");
  banner.className =
    "relative flex min-w-[340px] rotate-3 -scale-x-100 items-center justify-center gap-3 rounded-lg border-2 px-5 py-2 opacity-90 shadow";
  banner.style.backgroundColor = tint(candidateColor(opts.votingFor), 0.86);
  banner.style.borderColor = candidateColor(opts.votingFor);
  const signFace = document.createElement("img");
  signFace.src = candidateImage(opts.votingFor);
  signFace.alt = "";
  signFace.className = "h-10 w-10 shrink-0 object-contain";
  const signText = document.createElement("div");
  signText.className = "whitespace-nowrap text-lg font-bold";
  signText.textContent = `Voto por ${displayName(opts.votingFor)}`;
  banner.append(signFace, signText);

  held.append(stick, banner); // stick first → renders behind the banner

  // ── State + toggle ───────────────────────────────────────────────────────
  let shown = false;
  const render = () => {
    held.classList.toggle("hidden", !shown);
    msg.textContent = shown
      ? `Estás mostrando "Voto por ${displayName(opts.votingFor)}"`
      : "Nadie sabe por quién votas 🤫";
    toggle.textContent = shown
      ? "Bajar el cartel 🙈"
      : "Mostrar mi candidato 🪧";
  };
  toggle.addEventListener("click", () => {
    shown = !shown;
    render();
  });
  render();

  // ── Proximity action button (bottom-centre): chat / ver cartelera ─────────
  // Centred wrapper so the button's nudge animation (translateY) doesn't fight
  // a centring transform; lifted above the joystick on portrait.
  const actionWrap = document.createElement("div");
  actionWrap.className =
    "pointer-events-none fixed inset-x-0 bottom-8 z-20 flex flex-col items-center gap-1 portrait:bottom-56";

  // Attention hint shown above the button as you approach: a short label and a
  // bouncing down-arrow pointing at the action button, so it's obvious you can
  // interact (e.g. "haz clic para leer" at a wall).
  const hint = document.createElement("div");
  hint.className = "hidden flex-col items-center gap-0.5 [&:not(.hidden)]:flex";
  const hintText = document.createElement("span");
  hintText.className =
    "rounded-full border-2 border-black bg-white px-3 py-1 text-sm font-bold shadow";
  const hintArrow = document.createElement("span");
  hintArrow.className = "animate-bounce text-2xl leading-none";
  hintArrow.textContent = "⬇️";
  hint.append(hintText, hintArrow);

  const actionBtn = document.createElement("button");
  actionBtn.className =
    "pointer-events-auto hidden animate-nudge rounded-full border-2 border-black bg-white px-5 py-3 text-lg font-bold shadow-lg";
  actionWrap.append(hint, actionBtn);
  let actionKey: string | null = null;
  let actionClick: (() => void) | null = null;
  actionBtn.addEventListener("click", () => actionClick?.());

  // Help hub button (bottom-right).
  const helpBtn = document.createElement("button");
  helpBtn.className =
    "fixed bottom-6 right-6 z-20 h-12 w-12 rounded-full border-2 border-black bg-white text-xl shadow-lg";
  helpBtn.textContent = "❓";
  helpBtn.title = "Ayuda";
  helpBtn.addEventListener("click", () => opts.onHelp());

  // Exit the party → reopen the intro (avatar/candidate) modal.
  const exitBtn = document.createElement("button");
  exitBtn.className =
    "fixed bottom-6 right-20 z-20 h-12 rounded-full border-2 border-black bg-white px-3 text-sm font-bold shadow-lg";
  exitBtn.textContent = "🚪 Salir";
  exitBtn.title = "Cambiar avatar / candidato";
  exitBtn.addEventListener("click", () => opts.onExit());

  document.body.append(panel, held, actionWrap, helpBtn, exitBtn);

  return {
    setAction(key, label, onClick, hintLabel) {
      if (key === actionKey) return;
      actionKey = key;
      if (key) {
        actionBtn.textContent = label;
        actionClick = onClick;
        actionBtn.classList.remove("hidden");
        if (hintLabel) {
          hintText.textContent = hintLabel;
          hint.classList.remove("hidden");
        } else {
          hint.classList.add("hidden");
        }
      } else {
        actionClick = null;
        actionBtn.classList.add("hidden");
        hint.classList.add("hidden");
      }
    },
    pulseHelp() {
      helpBtn.classList.add("animate-nudge");
      setTimeout(() => helpBtn.classList.remove("animate-nudge"), 4500);
    },
    dispose() {
      panel.remove();
      held.remove();
      actionWrap.remove();
      helpBtn.remove();
      exitBtn.remove();
    },
  };
}
